import React, { useCallback, useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import {
  REOWN_PROJECT_ID,
  appKitInitError,
  isAppKitReady,
  probeWalletConnectRelay,
} from '../providers/ReownProvider';
import { hasInjectedProvider, injectedEvmProvider, injectedSolanaProvider } from '../helpers/injectedWallets';
import { apiBaseUrl } from '../helpers/apiBase';

const { Text, Title } = Typography;

/**
 * Диагностика подключения кошельков. Открывается по ?wtdiag=1.
 *
 * Зачем отдельный экран. Симптом «кошелёк открывается, но подписывать нечего»
 * снаружи неотличим от десятка разных причин: не поднялся AppKit, не доходит
 * relay, эндпоинт привязки закрыт заголовками приложения, кошелёк не вставил
 * провайдер. Гадать по одному симптому дорого — особенно на чужом телефоне,
 * куда не подключить отладчик.
 *
 * Проверка обязана быть способна ответить «нет». Предыдущая версия этого
 * экрана не могла: строка «домен разрешён в проекте» дёргала
 * api.web3modal.org/appkit/v1/config, который отдаёт 200 любому домену (сверено
 * запросами с Origin проекта и с Origin постороннего сайта — оба 200), а
 * строка «relay отвечает» открывала сокет с параметром auth=diag, на который
 * relay отвечает 401 всегда: подпись обязательна, и негодная подпись
 * отвергается раньше, чем relay смотрит на projectId. То есть первая строка
 * горела зелёным всегда, вторая красным всегда, и обе — независимо от
 * состояния проекта. По второй владелец полдня искал поломку в projectId,
 * которой не было.
 *
 * Здесь каждая строка получена тем же кодом, который делает настоящую работу:
 * relay проверяется клиентом WalletConnect с его настоящей подписью,
 * доступность привязки — тем же запросом, который шлёт страница внутри
 * кошелька.
 */

type CheckState = 'pending' | 'ok' | 'fail';

interface Check {
  name: string;
  state: CheckState;
  detail: string;
}

function ua(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent : '';
}

/**
 * Доступен ли эндпоинт привязки без заголовков приложения.
 *
 * Именно эта дверь была закрыта: security_middleware.php требует JWT или
 * X-App-Token, а во встроенном браузере кошелька нет ни того ни другого — это
 * отдельный WebView, входа в нём не было. Привязка получала 403 «Access
 * denied» до того, как сервер успевал посмотреть на одноразовый токен, и
 * человек видел «Кошелёк не привязан. Попробуйте ещё раз» сколько угодно раз
 * подряд.
 *
 * Токен здесь заведомо негодный: 200 с valid=false — это правильный ответ,
 * означающий, что дверь открыта и настоящий токен будет проверен по существу.
 */
async function checkLinkEndpoint(): Promise<Check> {
  const name = 'Привязка кошелька: эндпоинт открыт';
  const url = `${apiBaseUrl()}/api/wallet/link-session.php?token=wtdiag-probe-not-a-real-token`;
  try {
    const res = await fetch(url, { method: 'GET', credentials: 'omit' });
    const body = await res.text();
    if (res.status === 403) {
      return {
        name,
        state: 'fail',
        detail:
          'HTTP 403. Запрос без заголовков приложения не пропускается — из браузера кошелька привязка невозможна. Это и есть причина «Кошелёк не привязан».',
      };
    }
    if (!res.ok) {
      return { name, state: 'fail', detail: `HTTP ${res.status}. ${body.slice(0, 200)}` };
    }
    return { name, state: 'ok', detail: `HTTP 200. ${body.slice(0, 120)}` };
  } catch (e: any) {
    return {
      name,
      state: 'fail',
      detail: `Запрос не прошёл: ${e?.message || e}. Либо сеть, либо CORS с ${window.location.origin}.`,
    };
  }
}

async function checkRelay(): Promise<Check> {
  const { ok, detail } = await probeWalletConnectRelay();
  return { name: 'WalletConnect: relay принял подпись', state: ok ? 'ok' : 'fail', detail };
}

export const WalletDiagnostics: React.FC = () => {
  const [checks, setChecks] = useState<Check[]>([]);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    const evm = injectedEvmProvider();
    const sol = injectedSolanaProvider();

    const appKitCheck: Check = {
      name: 'AppKit поднялся',
      state: isAppKitReady() ? 'ok' : 'fail',
      detail: appKitInitError() || (isAppKitReady() ? 'createAppKit отработал.' : 'Экземпляра нет.'),
    };
    const injectedCheck: Check = {
      name: 'Провайдер в странице (встроенный браузер кошелька)',
      state: evm || sol || hasInjectedProvider('tron') ? 'ok' : 'fail',
      detail: [
        `EVM: ${evm ? 'есть' : 'нет'}`,
        `Solana: ${sol ? 'есть' : 'нет'}`,
        `TRON: ${hasInjectedProvider('tron') ? 'есть' : 'нет'}`,
      ].join(' · '),
    };
    setChecks([appKitCheck, injectedCheck]);

    const [link, relay] = await Promise.all([checkLinkEndpoint(), checkRelay()]);
    setChecks([appKitCheck, link, relay, injectedCheck]);
    setRunning(false);
  }, []);

  useEffect(() => {
    run();
  }, [run]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000001,
        background: '#0b1220',
        color: '#e6edf7',
        overflowY: 'auto',
        padding: '24px 18px 48px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12,
        lineHeight: 1.55,
      }}
    >
      <Title level={4} style={{ color: '#e6edf7', marginTop: 0 }}>
        Диагностика кошельков
      </Title>

      <div style={{ marginBottom: 16, color: '#93a4bd' }}>
        <div>origin: {typeof window !== 'undefined' ? window.location.origin : '—'}</div>
        <div>api: {apiBaseUrl()}</div>
        <div>projectId: {REOWN_PROJECT_ID}</div>
        <div style={{ wordBreak: 'break-all' }}>ua: {ua()}</div>
      </div>

      {checks.map((check) => (
        <div
          key={check.name}
          style={{
            borderLeft: `3px solid ${check.state === 'ok' ? '#22c55e' : check.state === 'fail' ? '#ef4444' : '#64748b'}`,
            padding: '8px 0 8px 10px',
            marginBottom: 10,
            background: 'rgba(255,255,255,0.03)',
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {check.state === 'ok' ? '✓' : check.state === 'fail' ? '✕' : '…'} {check.name}
          </div>
          <div style={{ color: '#93a4bd', wordBreak: 'break-word' }}>{check.detail}</div>
        </div>
      ))}

      <Button onClick={run} loading={running} style={{ marginTop: 12 }}>
        Проверить снова
      </Button>
      <Text style={{ display: 'block', color: '#64748b', marginTop: 16 }}>
        «Привязка кошелька» — путь через встроенный браузер кошелька: Trust,
        Bitget, OKX, TronLink, Phantom. «relay принял подпись» — путь через
        WalletConnect: MetaMask и всё, что подключается без выхода из браузера.
        Пути независимы, красное в одном не объясняет другое.
      </Text>
    </div>
  );
};

export default WalletDiagnostics;
